import { useEffect, useState } from 'react';
import type { IntegrationProvider } from '@lizrd/contracts';
import { ConnectSourceModal } from './ConnectSourceModal';
import { AddIntegrationModal } from './AddIntegrationModal';
import { AwsSetupModal } from './AwsSetupModal';
import { GcpSetupModal } from './GcpSetupModal';
import { AzureSetupModal } from './AzureSetupModal';

/**
 * The single source of truth for "connect a source": the provider picker plus every
 * provider-specific setup modal, with the picker → modal routing in ONE place so the
 * onboarding Connect step and the Domain → Integrations page always offer the SAME
 * modal for each provider. AWS/GCP/Azure each open their dedicated multi-step setup;
 * GitHub/GitLab use the shared AddIntegrationModal (repo/install and token flows).
 *
 * Sync behaviour is decided on the SERVER, not here: connecting while the org is in
 * ONBOARDING only validates access (the onboarding Sync step runs the first sync),
 * whereas an ACTIVE org syncs immediately on connect. This component never triggers a
 * sync itself, so it is identical for both callers.
 */
export function ConnectSourceFlow({
  envId,
  active,
  kind,
  directProvider,
  onDirectHandled,
  onClosePicker,
  onConnected,
}: {
  envId: string;
  /** Whether the provider picker is open. */
  active: boolean;
  /** Restrict the picker to one family (onboarding's per-box buttons); omit for all. */
  kind?: 'cloud' | 'code';
  /** When the caller already knows the provider (e.g. onboarding's per-provider
   * buttons), open that provider's setup modal directly — skipping the picker. */
  directProvider?: IntegrationProvider | null;
  /** Called once a directProvider has been routed, so the caller can reset it. */
  onDirectHandled?: () => void;
  onClosePicker: () => void;
  /** Called after a source is connected, with the provider that was added. */
  onConnected: (provider: IntegrationProvider) => void;
}) {
  const [addProvider, setAddProvider] = useState<IntegrationProvider | null>(null);
  const [awsOpen, setAwsOpen] = useState(false);
  const [gcpOpen, setGcpOpen] = useState(false);
  const [azureOpen, setAzureOpen] = useState(false);

  // Open a provider's dedicated setup modal (AWS/GCP/Azure) or the shared
  // add-integration modal (GitHub/GitLab). The routing lives here so both callers
  // — the picker and the direct launch — always reach the SAME modal.
  function route(provider: IntegrationProvider) {
    if (provider === 'AWS') setAwsOpen(true);
    else if (provider === 'GCP') setGcpOpen(true);
    else if (provider === 'AZURE') setAzureOpen(true);
    else setAddProvider(provider);
  }

  function handlePick(provider: IntegrationProvider) {
    onClosePicker();
    route(provider);
  }

  // Direct launch: the caller picked the provider, so go straight to its modal.
  useEffect(() => {
    if (directProvider) {
      route(directProvider);
      onDirectHandled?.();
    }
  }, [directProvider]);

  return (
    <>
      {active && <ConnectSourceModal kind={kind} onClose={onClosePicker} onPick={handlePick} />}
      {addProvider && (
        <AddIntegrationModal
          envId={envId}
          provider={addProvider}
          onClose={() => setAddProvider(null)}
          onConnected={() => {
            onConnected(addProvider);
            setAddProvider(null);
          }}
        />
      )}
      {awsOpen && (
        <AwsSetupModal
          envId={envId}
          onClose={() => setAwsOpen(false)}
          onConnected={() => {
            setAwsOpen(false);
            onConnected('AWS');
          }}
        />
      )}
      {gcpOpen && (
        <GcpSetupModal
          envId={envId}
          onClose={() => setGcpOpen(false)}
          onConnected={() => {
            setGcpOpen(false);
            onConnected('GCP');
          }}
        />
      )}
      {azureOpen && (
        <AzureSetupModal
          envId={envId}
          onClose={() => setAzureOpen(false)}
          onConnected={() => {
            setAzureOpen(false);
            onConnected('AZURE');
          }}
        />
      )}
    </>
  );
}
